import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Divider, Modal } from '@mui/material';
import ExerciseLight from '../../../assets/icons/exerciseLight.svg';
import ImproveTreatment from '../../../assets/icons/improveTreatment.svg';
import { ArcContainer } from '../../../shared/components/arcContainer';
import { DoubleTextBlueCard } from '../../../shared/components/doubleTextBlueCard';
import { BlueHeaderWithText } from './blueHeaderWithText';
import PalSelector from './palSelector';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';

import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { displayCalculation } from '../../../shared/utils/utilFunctions';
import {
  setPengPal,
  setPengReePlusPal
} from '../../../store/slices/metaDataSlice';
import { getReePlusPal } from '../../../shared/utils/peng2018';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { TypographyH1Bold, TypographyH4Bold } from './style';
import { TextWithInfoIcon } from '../../../shared/components/textWithInfoIcon';
import InfoIcon from '../../../assets/icons/infoIcon.svg';
import { MixedModal } from '../../../shared/components/modal/MixedModal';
import { AddOnInfoModal } from '../../../shared/components/modal/addOnInfoModal';
export const PalScreen = () => {
  const dispatch = useDispatch();
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const { estimatingRequirements, patientInformation } =
    useAppSelector(metaDataSelector);
  const { peng2018 } = estimatingRequirements;
  const { ree2ndVal, palValue, reePlusPal, disease } = peng2018;

  useEffect(() => {
    disease?.id !== 'otherOverweightAbove65' &&
    disease?.id !== 'otherOverweightBelow65'
      ? dispatch(setPengReePlusPal(getReePlusPal(ree2ndVal, palValue)))
      : dispatch(
          setPengReePlusPal(getReePlusPal(disease.calcValues, palValue))
        );
  }, [palValue]);

  useEffect(() => {
    dispatch(setPengPal(Number(minValue)));
  }, [minValue]);

  return (
    <>
      <TextWithInfoIcon
        title={'Peng 2018'}
        icon={InfoIcon}
        fontSize={'1.625rem'}
        lineHeight={'1.375rem'}
        variant={'h1'}
        handleIconClick={() => setOpenInfoModal(true)}
        testId="PengInfoPopup"
      />
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          backgroundColor: '#F2F2F2',
          p: 0,
          top: '100px',
          left: 0
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          <BlueHeaderWithText
            text1="REE (kcal/day):"
            value1={
              disease?.id !== 'otherOverweightAbove65' &&
              disease?.id !== 'otherOverweightBelow65'
                ? displayCalculation(ree2ndVal)
                : displayCalculation(disease.calcValues)
            }
            single={true}
            customWidth="90%"
          />
        </Box>
      </Box>

      <Box sx={{ marginTop: '200px' }}>
        <ArcContainer icon={ExerciseLight}>
          <TypographyH4Bold
            variant="h4"
            sx={{
              textAlign: 'center',
              marginTop: '1.875rem'
            }}
            className="pt-16"
          >
            PAL
          </TypographyH4Bold>
          <PalSelector
            setPalScreenMinValue={setMinValue}
            setPalScreenMaxValue={setMaxValue}
          />
          <Divider
            sx={{
              backgroundColor: 'rgba(255,255,255,0.4)',
              my: 2,
              width: '60%',
              mx: 'auto'
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1.25rem'
            }}
          >
            <SingleNumberCard
              width={'50%'}
              title={'PAL'}
              marginVal={'0.625rem'}
              incrementDecrementVal={0.01}
              value={Number(palValue)}
              setValue={(val: number) => dispatch(setPengPal(Number(val)))}
              range={{ min: minValue, max: maxValue }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <DoubleTextBlueCard
              heading1="REE + Activity Factor"
              label1={displayCalculation(reePlusPal)}
              unit1={' kcal/day'}
              icon={ImproveTreatment}
            />
          </Box>
        </ArcContainer>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
        <NavigationButtons
          nextRoute={
            patientInformation.type === 'PAEDIATRIC'
              ? '/estimating-requirements/paediatric-energy'
              : '/estimating-requirements/protein'
          }
          nextDisabled={false}
        />
      </Box>
      <Modal
        open={openInfoModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={`REE`}
          description1={` = Resting Energy Expenditure`}
          heading2={`PAL`}
          description2={` = Physical Activity Level`}
          title={`Reference:`}
          content={`Todorovic VE. Mafrici B. A Pocket Guide to Clinical Nutrition 2018. 5th edition`}
          onClose={() => setOpenInfoModal(false)}
        />
      </Modal>
    </>
  );
};
